/**
 * Openapp.ts https://github.com/luojinghui/web-open-app
 *
 * Description: open and launch app in H5 page
 * Support: mobile and pc
 * @authors lijie44@baidu.com
 * @date 2023-5-17 16:18:30
 */

import {UA} from '@lib/utils/tools';
import brandScheme from './brandScheme';
import {
    ISystemInfo,
    systemInfo,
} from './browser';

interface IOpenStatus {
    FAILED: 'FAILED';
    SUCCESS: 'SUCCESS';
    UNKNOW: 'UNKNOW';
}

interface IOptions {
    timeout?: number;
    scheme: string;
    intent?: string;
    applink?: string;
    yyb?: string;
    appstore?: string;
    downloadUrl?: string;
    wechatJumpYYB?: boolean;
    iosUseScheme?: boolean;
    copyText?: string;
    callback: (status: string, msg?: string) => void;
}

interface IPrefix {
    property: string;
    eventName: string;
}

interface DeepLinkParams {
    appUrl: string;
    webUrl?: string;
    storeUrl?: string;
    marketUrl?: string;
}
interface DeepLink {
    isAndroid: boolean;
    isIOS: boolean;
    scheme: string;
    marketUrl?: string;
    webUrl?: string;
}

/**
 *
 * @param scheme baiduwanhua://
 * @param marketUrl 如果是android，使用android的marketUrl，如果是ios，则是苹果商店的url
 * @param url 最后兜底的webrul
 */
export const createDeepLink = ({
    isAndroid,
    isIOS,
    scheme,
    marketUrl,
    webUrl,
}: DeepLink) => {
    const params: DeepLinkParams = {
        appUrl: scheme,
        webUrl,
    };
    if (isAndroid && marketUrl) {
        params.marketUrl = marketUrl;
    }
    if (isIOS && marketUrl) {
        // 理论上不会走到这里
        params.storeUrl = marketUrl;
    }
    return `baiduboxapp://v7/vendor/ad/deeplink?params=${encodeURIComponent(JSON.stringify(params))}`;
};

export default class OpenApp {
    options: IOptions;
    openStatus!: IOpenStatus;
    timer: any;
    systemInfo!: ISystemInfo;
    private msg: string | undefined;

    constructor(options: IOptions) {
        const defaultOptions: IOptions = {
            timeout: 1000,
            scheme: '',
            intent: '',
            applink: '',
            yyb: '',
            appstore: '',
            downloadUrl: '',
            wechatJumpYYB: false,
            iosUseScheme: false,
            copyText: '',
            callback() {},
        };
        this.options = {
            ...defaultOptions, ...options,
        };
        this.init();
    }

    init() {
        // 打开状态，failed，success，unknow
        this.openStatus = {
            FAILED: 'FAILED',
            SUCCESS: 'SUCCESS',
            UNKNOW: 'UNKNOW',
        };
        this.systemInfo = systemInfo();
    }

    open() {
        const {applink, scheme, intent, iosUseScheme} = this.options;
        const {
            weixinVersion,
            isIOS,
            // isWeixin,
            isAndroid,
            isChrome,
            isSafari,
            isFirefox,
            isIPad,
            isBaidu,
        } = this.systemInfo as any;
        const isWeixin = (function () {
            // @ts-ignore
            return (/MicroMessenger/i.exec(UA)) === 'micromessenger';
        })();

        this.timeoutEvent();

        if (isIOS) {
            // 微信7.0.5放开了universal link，直接使用即可
            // 否则，直接反馈唤起失败，让调用者决定是否需要采取其他方式
            if (isWeixin && weixinVersion < '7.0.5') {
                this.msg = 'isIosLowVersionWechat';
                this.locationCall(scheme);
            }
            else if (applink) {
                this.locationCall(applink);
            }
            else if (iosUseScheme && scheme) {
                this.locationByIframeCall(scheme);
                this.locationCall(scheme);
            }
        }
        else if (isIPad && iosUseScheme) {
            // this.locationByIframeCall(scheme);
            this.locationCall(scheme);
        }
        else if (isAndroid) {
            if (isWeixin) {
                this.msg = 'isAndroidWechat';
            }

            if (isChrome && !isWeixin) {
                if (intent) {
                    this.locationCall(intent);
                    this.locationCall(scheme);
                    return;
                }
            }

            if (isBaidu) {
                const dpScheme = createDeepLink({
                    isAndroid,
                    isIOS,
                    scheme,
                    // 如果此处开启市场，则优先调用市场，但产品需要优先启用下载渠道号，逻辑放在上层
                    marketUrl: brandScheme(UA, true),
                });
                this.locationCall(dpScheme);
                return;
            }

            this.locationCall(scheme);
        }
        else if (isSafari || isFirefox) {
            // safari和firefox调用iframe唤起应用
            // 这两个浏览器通过scheme唤起时，如果不存在应用，那么会跳转并提示警告信息
            this.locationByIframeCall(scheme);
        }
        else {
            this.locationCall(scheme);
        }
    }

    download() {
        const {wechatJumpYYB, downloadUrl, yyb, appstore} = this.options;
        const {isIOS, isWeixin, isAndroid} = this.systemInfo as any;

        if (isWeixin && isAndroid) {
            if (wechatJumpYYB && yyb) {
                this.locationCall(yyb);
            }
        }
        else if (isWeixin && isIOS) {
            if (wechatJumpYYB && appstore) {
                this.locationCall(appstore);
            }
        }
        else if (isAndroid) {
            if (downloadUrl) {
                this.locationCall(downloadUrl);
            }
            else {
                if (yyb) {
                    this.locationCall(yyb);
                }
            }
        }
        else if (isIOS && appstore) {
            this.locationCall(appstore);
        }
        else {
            if (downloadUrl) {
                this.locationCall(downloadUrl);
                return;
            }
            if (yyb) {
                this.locationCall(yyb);
                return;
            }

        }
    }

    timeoutEvent() {
        let haveChange = false;
        let isHaveApp = false;
        let isInputTimeout = false;
        let timeout = this.options.timeout || 1000;
        const {property = '', eventName = ''} = this.getVisibilityPrefix();
        const {isIOS, isSafari} = this.systemInfo as any;

        // 三星浏览器存在一定的页面跳转延迟，如果设置小于1s，那么再加上300ms时间
        if (this.systemInfo?.isSamsung) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            timeout = timeout <= 1000 ? timeout + 300 : timeout;
        }

        const pageChange = (e: any) => {
            const isHide
        = document[property as keyof Document] || e.hidden || document.visibilityState === 'hidden';

            haveChange = true;

            if (isHide) {
                isHaveApp = true;
            }

            document.removeEventListener(eventName, pageChange);
            document.removeEventListener('baiduboxappvisibilitychange', pageChange);
        };

        const pageBlur = () => {
            haveChange = true;
            isHaveApp = true;
        };

        window.addEventListener('blur', pageBlur);
        document.addEventListener(eventName, pageChange, false);
        document.addEventListener('baiduboxappvisibilitychange', pageChange, false);

        // 如果ios使用safari浏览器，那么
        if (!(isIOS && isSafari && this.options.iosUseScheme)) {
            const input = document.createElement('input');
            const timmer = null;
            const onBlur = function () {
                if (!isInputTimeout) {
                    haveChange = true;
                    isHaveApp = true;
                }
            };

            input.setAttribute('id', 'input');
            input.setAttribute('readonly', '');
            input.setAttribute('autocomplete', 'off');
            input.setAttribute('type', 'text');
            if (this.options.copyText) {
                input.setAttribute('value', this.options.copyText);
            }
            input.setAttribute(
                'style',
                'opacity: 0; position: fixed; top: 0; left: -999px; width: 1px; height: 1px'
            );
            document.body.appendChild(input);
            input.addEventListener('blur', onBlur);

            setTimeout(() => {
                isInputTimeout = true;
                input.removeEventListener('blur', onBlur);
                timmer && clearInterval(timmer);

                input.remove();
            }, this.options.timeout);

            input.focus();

            if (this.options.copyText) {
                input.select();
                input.setSelectionRange(0, input.value.length);
                document.execCommand('copy');
            }
        }

        this.timer = setTimeout(() => {
            if (
                (haveChange && isHaveApp)
                || document.visibilityState === 'hidden'
                || document.hidden
            ) {
                this.openEnd(this.openStatus.SUCCESS);

                return;
            }

            if (haveChange) {
                return;
            }

            // @ts-ignore
            const isHidden = document.visibilityState === 'hidden';

            window.removeEventListener('blur', pageBlur);
            document.removeEventListener(eventName, pageChange);
            document.removeEventListener('baiduboxappvisibilitychange', pageChange);

            if (!(isHidden || document.hidden) && !haveChange) {
                this.openEnd(this.openStatus.FAILED);
            }
            else {
                this.openEnd(this.openStatus.UNKNOW);
            }

            haveChange = true;
        }, this.options.timeout);
    }

    getVisibilityPrefix(): IPrefix {
        const prefixes = [
            {
                property: 'webkit',
                eventName: 'webkitvisibilitychange',
            },
            {
                property: 'moz',
                eventName: 'mozvisibilitychange',
            },
            {
                property: 'ms',
                eventName: 'msvisibilitychange',
            },
            {
                property: 'o',
                eventName: 'ovisibilitychange',
            },
        ];

        let correctPrefix: any = {};

        if ('hidden' in document) {
            return {
                property: 'hidden',
                eventName: 'visibilitychange',
            };
        }

        prefixes.forEach(prefix => {
            if (prefix.property + 'Hidden' in document) {
                correctPrefix = prefix;
            }
        });

        return correctPrefix;
    }

    openEnd(status: string) {
        clearTimeout(this.timer);

        this.options.callback(status, this.msg);
        this.msg = '';

        // 调起失败处理
        if (status !== this.openStatus.SUCCESS) {
            switch (status) {
                case 'FAILED':
                    break;
                case 'UNKNOW':
                    break;
                default:
                    break;
            }
        }
    }

    locationCall(url: string) {
        if (window.location) {
            // eslint-disable-next-line xss/no-location-href-assign
            window.location.href = url;
        }
        else if (document.location) {
            // eslint-disable-next-line xss/no-location-href-assign
            document.location.href = url;
        }
    }

    locationByIframeCall(url: string) {
        const iframe = document.createElement('iframe');

        iframe.setAttribute('src', url);
        iframe.setAttribute('style', 'display:none');

        document.body.appendChild(iframe);
        setTimeout(() => {
            document.body.removeChild(iframe);
        }, 300);
    }
}
